<script setup>
import { ref, computed, onMounted, watch } from "vue";
import { useField } from "vee-validate";
import * as yup from "yup";
import AutoComplete from "primevue/autocomplete";
import { useBookingStore } from "@stores/BookingStore";

const props = defineProps({
  formName: String,
  name: String,
  label: String,
  placeholder: String,
  description: String,
  data: Array,
  multiple: Boolean,
  optionValue: {
    // The key in the data object to use as the value
    type: String,
    default: "",
  },
  optionLabel: {
    type: String,
    default: "",
  },
  modelValue: {
    type: [Array, Object, String],
    default: () => [],
  },
  showRequired: {
    type: Boolean,
    default: true,
  },
  errorPosition: {
    type: String,
    default: "static",
  },
  type: {
    type: String,
    default: "text",
  },
  required: {
    type: Boolean,
    default: false,
  },
});

const bookingStore = useBookingStore();
const field = props.name;
const items = ref([]);
const selectedLanguage = ref(null);
const placeholderText = computed(() =>
  Array.isArray(value.value) && value.value.length ? "" : props.placeholder
);

const decodeHtmlEntities = (text) => {
  if (typeof text !== "string") return text;
  // Using DOMParser is a standard way to decode HTML entities
  const parser = new DOMParser();
  const doc = parser.parseFromString(text, "text/html");
  return doc.documentElement.textContent || text; // Fallback to original text
};

const processedData = computed(() => {
  if (!props.data || props.data.length === 0) {
    return [];
  }

  if (
    props.optionLabel &&
    typeof props.data[0] === "object" &&
    props.data[0] !== null
  ) {
    return props.data.map((item) => ({
      ...item,
      [props.optionLabel]: decodeHtmlEntities(item[props.optionLabel]),
    }));
  } else if (typeof props.data[0] === "string") {
    return props.data.map((item) => decodeHtmlEntities(item));
  }
  return props.data; // Fallback for unexpected data structure
});

const { value, errorMessage, validate } = useField(
  props.name,
  yup.lazy((value) => {
    const isArray = Array.isArray(value);
    const isObject = typeof value === "object" && value !== null;

    if (!props.required) {
      return isArray ? yup.array() : isObject ? yup.object() : yup.string();
    }

    // For required fields: arrays need min(1) since required() only checks existence
    if (isArray) {
      return yup.array().min(1, `${props.label} is required.`);
    }

    return isObject
      ? yup.object().required(`${props.label} is required.`)
      : yup.string().required(`${props.label} is required.`);
  }),
  {
    initialValue: props.multiple
      ? (Array.isArray(props.modelValue) ? props.modelValue : [])
      : (props.modelValue || null)
  }
);

const onSelect = () => {};
const onClear = () => {};

const search = (event) => {
  const query = event.query.trim().toLowerCase();
  if (query === "") {
    items.value = [];
    return;
  }

  const sourceData = processedData.value; // Use the decoded data
  if (!sourceData || sourceData.length === 0) {
    items.value = [];
    return;
  }

  if (
    props.optionLabel &&
    typeof sourceData[0] === "object" &&
    sourceData[0] !== null
  ) {
    items.value = sourceData
      .filter((item) => {
        const labelValue = item[props.optionLabel]; // This will be the decoded string
        return (
          typeof labelValue === "string" &&
          labelValue.toLowerCase().startsWith(query)
        );
      })
      .map((item) => {
        if (props.optionValue) {
          return {
            [props.optionValue]: item[props.optionValue],
            [props.optionLabel]: item[props.optionLabel],
          };
        }
        return item;
      });
  } else if (typeof sourceData[0] === "string") {
    // If data is an array of strings
    items.value = sourceData.filter(
      (
        item // item here is a decoded string
      ) => item.toLowerCase().startsWith(query)
    );
  } else {
    items.value = []; // Fallback for unexpected data structure
  }
};

// Define emits to declare event emission capability
const emit = defineEmits(["update:modelValue"]);

watch(value, (newValue) => {
  emit("update:modelValue", newValue);
});
</script>

<template>
  <fieldset class="frm-field">
    <div class="mb-10" :class="{ 'flex justify-between': $slots.label }">
      <label :for="name" class="block">
        {{ label }}
        <span v-if="required && showRequired" class="text-red-500">*</span>
      </label>
      <slot name="label"></slot>
    </div>

    <div class="z-10 relative flex gap-2 px-0 frm-input">
      <AutoComplete
        class="w-full"
        :optionLabel="optionLabel"
        v-model="value"
        :id="name"
        :name="name"
        :minLength="2"
        :placeholder="placeholderText"
        :suggestions="items"
        :multiple="multiple"
        @complete="search"
        forceSelection
        @blur="validate"
        @item-select="onSelect"
        @item-unselect="onClear">
      </AutoComplete>
    </div>
    <span
      v-if="errorMessage"
      class="block mt-10 text-14 text-error leading-none"
      :class="{ 'md:absolute': errorPosition === 'absolute' }">
      {{ errorMessage }}
    </span>
    <span v-if="description" class="frm-desc">
      {{ description }}
    </span>
  </fieldset>
</template>
